import { useRouter } from 'next/router'
import React from 'react'

import clsx from 'clsx'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { DeviceOrderData } from '@/lib/schemes'
import { Device } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import { useDeviceOrderDialog } from '@/hooks/dialogs/useDeviceOrderDialog'

import { Agreement } from '@/components/Agreement'
import Button from '@/components/Button'
import Rule from '@/components/Rule'
import { Dialog, SpecificDialogProps } from '@/components/dialogs/Dialog'
import { DeviceOrderCard } from '@/components/features/shop/DeviceOrderCard'
import { DeviceOrderDetails } from '@/components/features/shop/DeviceOrderDetails'
import Form from '@/components/form/Form'

interface DeviceInstallmentDialogProps extends SpecificDialogProps {
  device: Device
  form: UseFormReturn<DeviceOrderData>
  setDeviceOrderSchema: (value: any) => void
}

export const DeviceInstallmentDialog = ({
  isOpen,
  onClose,
  device,
  form,
  setDeviceOrderSchema
}: DeviceInstallmentDialogProps) => {
  const { locale } = useRouter()
  const { formatMessage } = useIntl()

  const { onSubmit, estimate, isLoading, isEstimateLoading, handleCloseModal } =
    useDeviceOrderDialog({
      form,
      isOpen,
      device,
      onClose,
      setDeviceOrderSchema,
      isInstallment: true
    })

  const { handleSubmit } = form

  return (
    <Dialog
      {...{
        isOpen,
        onClose: handleCloseModal,
        title: formatMessage({
          id: 'ordering.installment.title',
          defaultMessage: 'Оформление заказа в рассрочку'
        }),
        className: clsx(
          'no-scrollbar !max-w-[806px] overflow-auto tablet:!h-[min(90vh,720px)]',
          'tablet:overflow-hidden tablet:rounded-2xl'
        )
      }}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={clsx(
            'scroll-wrap -mr-2 overflow-hidden',
            'overflow-y-auto pr-2 tablet:-mr-3 tablet:pr-3'
          )}
        >
          <div className="flex w-full flex-col gap-4 pb-4 tablet:gap-6 tablet:pb-5">
            <DeviceOrderCard
              isDialogCard
              {...{ device, form, estimate, isLoading: isEstimateLoading }}
            />
            <DeviceOrderDetails
              {...{
                device,
                form,
                estimate,
                isInstallment: true,
                isLoading: isEstimateLoading
              }}
            />
          </div>
        </div>
        <Rule className="-ml-5 w-[calc(100%+40px)]" />
        <div className="flex justify-between pt-3 max-tablet:flex-col-reverse max-tablet:gap-2 tablet:pt-5">
          <Agreement className="!text-x-base max-tablet:text-center tablet:max-w-[280px]" />
          <div className="flex gap-2.5 tablet:justify-end">
            <Button
              className="min-h-11 flex-2 tablet:w-fit tablet:flex-none tablet:!px-7"
              isLoading={isLoading}
            >
              <FormattedMessage
                {...{
                  id: 'button.pay',
                  defaultMessage: 'Оплатить'
                }}
              />{' '}
              {!!estimate?.total &&
                formatCurrency(estimate?.total, device.currency, locale)}
            </Button>
          </div>
        </div>
      </Form>
    </Dialog>
  )
}
